/**
 * UserDetailPage — 用户详情
 * 展示用户完整信息；切换状态 / 删除操作放在此处（列表行不再直接提供）。
 */
import { useState } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Spinner,
  Avatar,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
} from '@fluentui/react-components'
import { DeleteRegular } from '@fluentui/react-icons'
import { usersApi } from '@/api/users'
import type { User } from '@/api/types'
import { toast } from '@/lib/toast'
import { ApiError } from '@/api/client'
import { ROUTES } from '@/routes'
import DetailLayout, { DetailSection, DetailRow } from '@/components/DetailLayout'
import DangerButton from '@/components/DangerButton'
import { useDetailItem } from '@/hooks/useDetailItem'
import { formatDate, formatQuota } from '@/lib/format'

const useStyles = makeStyles({
  deleteName: {
    fontWeight: 600,
    color: tokens.colorStatusDangerForeground1,
  },
})

export default function UserDetailPage() {
  const styles = useStyles()
  const { id } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { item, isLoading } = useDetailItem<User>(
    ['users'],
    () => usersApi.list(),
    id as string,
    'id',
  )

  const stateItem = (location.state as { item?: User } | null)?.item

  const [deleteOpen, setDeleteOpen] = useState(false)

  const toggleMutation = useMutation({
    mutationFn: () =>
      usersApi.update(Number(id), {
        active: !(item ?? stateItem)?.active,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      toast.success('操作成功', '用户状态已更新')
    },
    onError: (err) =>
      toast.error('操作失败', err instanceof ApiError ? err.message : '操作失败'),
  })

  const deleteMutation = useMutation({
    mutationFn: () => usersApi.delete(Number(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      toast.success('删除成功', '用户已删除')
      navigate(ROUTES.USERS)
    },
    onError: (err) =>
      toast.error('删除失败', err instanceof ApiError ? err.message : '删除失败'),
  })

  const username = item?.username ?? stateItem?.username ?? '用户详情'
  const email = item?.email ?? stateItem?.email

  return (
    <>
      <DetailLayout
        loading={isLoading && !stateItem}
        notFound={!isLoading && !item && !stateItem}
        notFoundText="未找到该用户"
        title={username}
        subtitle={email}
        media={<Avatar name={username} size={48} color="brand" />}
        actions={
          <>
            <Button
              appearance="subtle"
              onClick={() => toggleMutation.mutate()}
              disabled={toggleMutation.isPending || (!item && !stateItem)}
              icon={toggleMutation.isPending ? <Spinner size="tiny" /> : undefined}
            >
              {(item ?? stateItem)?.active ? '停用' : '启用'}
            </Button>
            <Button
              appearance="subtle"
              icon={<DeleteRegular />}
              onClick={() => setDeleteOpen(true)}
            >
              删除
            </Button>
          </>
        }
      >
        <DetailSection title="基本信息">
          <DetailRow label="ID" value={item?.id ?? stateItem?.id} />
          <DetailRow label="用户名" value={item?.username ?? stateItem?.username} />
          <DetailRow label="邮箱" value={item?.email ?? stateItem?.email} />
          <DetailRow
            label="显示名称"
            value={item?.display_name ?? stateItem?.display_name}
          />
          <DetailRow label="域" value={item?.domain ?? stateItem?.domain} />
          <DetailRow
            label="配额"
            value={formatQuota((item ?? stateItem)?.quota_bytes)}
          />
          <DetailRow
            label="管理员"
            value={(item ?? stateItem)?.is_admin ? '是' : '否'}
          />
          <DetailRow
            label="状态"
            value={(item ?? stateItem)?.active ? '活跃' : '停用'}
          />
          <DetailRow
            label="创建时间"
            value={formatDate((item ?? stateItem)?.created_at)}
          />
        </DetailSection>
      </DetailLayout>

      {/* 删除确认 */}
      <Dialog
        open={deleteOpen}
        onOpenChange={(_, d) => {
          if (!d.open) setDeleteOpen(false)
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>确认删除</DialogTitle>
            <DialogContent>
              <Text>
                确定要删除用户{' '}
                <span className={styles.deleteName}>
                  {item?.username ?? stateItem?.username}
                </span>{' '}
                吗？此操作不可撤销。
              </Text>
            </DialogContent>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '8px',
                paddingTop: '8px',
              }}
            >
              <Button onClick={() => setDeleteOpen(false)}>取消</Button>
              <DangerButton
                loading={deleteMutation.isPending}
                onClick={() => deleteMutation.mutate()}
              >
                确认删除
              </DangerButton>
            </div>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  )
}
